import { templatizeBffPath } from "@/lib/bffTransport";
import { reportSilentFailure } from "@/lib/reportSilentFailure";

/**
 * Sentry signal for "the BFF origin is unreachable from this page".
 *
 * A direct BFF call that the browser cannot read ends in `FETCH_ERROR`: a
 * dropped connection, but also every response without CORS headers — the
 * ALB's own 502/503/504 page when all BFF pods are down or a deploy is
 * rolling, a WAF reject, or a page origin the BFF's runtime CORS list does
 * not admit. The gateway status never reaches `isInfraFailure`, and
 * `rtkQueryCaptureSkipReason` drops every `FETCH_ERROR`, so without this the
 * outage is silent in Sentry after boot.
 *
 * One dropped connection must not page, so this reports only after
 * `DIRECT_BFF_UNREACHABLE_THRESHOLD` final (post-retry) failures inside
 * `DIRECT_BFF_UNREACHABLE_WINDOW_MS`, with no direct response in between, and
 * at most once per page. A failure while the browser reports itself offline
 * is not counted. Tags are PII-safe: method, templatized path, endpoint name
 * and connection type; never ids, query strings or bodies.
 */
export const DIRECT_BFF_UNREACHABLE_THRESHOLD = 3;
export const DIRECT_BFF_UNREACHABLE_WINDOW_MS = 60_000;
export const DIRECT_BFF_UNREACHABLE_FINGERPRINT = "direct-bff-unreachable";

let failureTimes: number[] = [];
let reported = false;

/** A direct call got a readable response: the BFF origin is reachable. */
export function noteDirectBffReached(): void {
  failureTimes = [];
}

/** A direct call ended in `FETCH_ERROR` after its retries (not aborted). */
export function noteDirectBffUnreachable({
  method,
  url,
  endpoint,
  now = Date.now(),
}: {
  method: string;
  /** The request path as the endpoint passed it; templatized here. */
  url: string;
  endpoint: string;
  now?: number;
}): void {
  if (reported) return;
  const nav = typeof navigator === "undefined" ? undefined : navigator;
  if (nav?.onLine === false) return;

  failureTimes = failureTimes.filter(
    (at) => now - at < DIRECT_BFF_UNREACHABLE_WINDOW_MS
  );
  failureTimes.push(now);
  if (failureTimes.length < DIRECT_BFF_UNREACHABLE_THRESHOLD) return;

  reported = true;
  const connection = (
    nav as (Navigator & { connection?: { effectiveType?: string } }) | undefined
  )?.connection;
  reportSilentFailure({
    message: "[Infra] Direct BFF origin unreachable — calls end in FETCH_ERROR",
    fingerprint: DIRECT_BFF_UNREACHABLE_FINGERPRINT,
    tags: {
      transport: "direct",
      method,
      path: templatizeBffPath(url),
      endpoint,
      effective_type: connection?.effectiveType ?? "unknown",
    },
    extra: {
      failures: failureTimes.length,
      windowMs: DIRECT_BFF_UNREACHABLE_WINDOW_MS,
    },
  });
}

export function resetDirectBffReachabilityForTests(): void {
  failureTimes = [];
  reported = false;
}
